import React from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import moment from 'moment-timezone';
import defaults from 'lodash/defaults';
import find from 'lodash/find';

import { xmSessionStorage } from '@xmatters/common/utils/xmStorage';

const getRelativeTimeRangeGetter = startTimeAgo => () => ({
  from: moment().subtract(startTimeAgo, 'minutes').format(),
});

const getRelativeDateRangeGetter = startDaysAgo => () => ({
  from: moment().subtract(startDaysAgo, 'days').format(),
});

const lastThirtyDaysOption = {
  value: 'PAST_30DAYS',
  label: <FormattedMessage id="PAST_30DAYS" />,
  getDateRangeGetter: () => getRelativeDateRangeGetter(30),
};

export const dateRangeFilterShape = PropTypes.shape({
  value: PropTypes.string.isRequired,
  label: PropTypes.node.isRequired,
  getDateRangeGetter: PropTypes.func.isRequired,
});

export const commonFilterOptions = {
  DEFAULT: lastThirtyDaysOption,
  PAST_15MINUTES: {
    value: 'PAST_15MINUTES',
    label: <FormattedMessage id="PAST_15MINUTES" />,
    getDateRangeGetter: () => getRelativeTimeRangeGetter(15),
  },
  PAST_1HOUR: {
    value: 'PAST_1HOUR',
    label: <FormattedMessage id="PAST_1HOUR" />,
    getDateRangeGetter: () => getRelativeTimeRangeGetter(60),
  },
  PAST_6HOURS: {
    value: 'PAST_6HOURS',
    label: <FormattedMessage id="PAST_6HOURS" />,
    getDateRangeGetter: () => getRelativeTimeRangeGetter(6 * 60),
  },
  PAST_12HOURS: {
    value: 'PAST_12HOURS',
    label: <FormattedMessage id="PAST_12HOURS" />,
    getDateRangeGetter: () => getRelativeTimeRangeGetter(12 * 60),
  },
  PAST_24HOURS: {
    value: 'PAST_24HOURS',
    label: <FormattedMessage id="PAST_24HOURS" />,
    getDateRangeGetter: () => getRelativeDateRangeGetter(1),
  },
  PAST_7DAYS: {
    value: 'PAST_7DAYS',
    label: <FormattedMessage id="PAST_7DAYS" />,
    getDateRangeGetter: () => getRelativeDateRangeGetter(7),
  },
  PAST_30DAYS: lastThirtyDaysOption,
  PAST_60DAYS: {
    value: 'PAST_60DAYS',
    label: <FormattedMessage id="PAST_60DAYS" />,
    getDateRangeGetter: () => getRelativeDateRangeGetter(60),
  },
  PAST_90DAYS: {
    value: 'PAST_90DAYS',
    label: <FormattedMessage id="PAST_90DAYS" />,
    getDateRangeGetter: () => getRelativeDateRangeGetter(90),
  },
  PAST_YEAR: {
    value: 'PAST_YEAR',
    label: <FormattedMessage id="PAST_YEAR" />,
    getDateRangeGetter: () => getRelativeDateRangeGetter(365),
  },
  DATE_RANGE: {
    value: 'DATE_RANGE',
    label: <FormattedMessage id="DATE_RANGE" />,
    getDateRangeGetter:
      ({ from, to } = { from: moment().format(), to: moment().format() }) =>
      () => ({ from, to }),
  },
  ALL_DATES: {
    value: 'ALL_DATES',
    label: <FormattedMessage id="ALL_DATES" />,
    // Intentionally blank; no to/from dates implying all time:
    getDateRangeGetter: () => () => ({}),
  },
};

export const getDefaultOptions = () => [
  commonFilterOptions.PAST_30DAYS,
  commonFilterOptions.PAST_60DAYS,
  commonFilterOptions.PAST_90DAYS,
  commonFilterOptions.DATE_RANGE,
];

export const getOption = (value, options = getDefaultOptions()) =>
  find(options, { value }) || commonFilterOptions.DEFAULT;

export const getDatesFromSessionStorage = storageKeyPrefix => {
  const from = xmSessionStorage.getItem(`${storageKeyPrefix}_from`);
  const to = xmSessionStorage.getItem(`${storageKeyPrefix}_to`);

  return {
    from: from !== 'undefined' && moment(from).isValid() ? from : undefined,
    to: to !== 'undefined' && moment(to).isValid() ? to : undefined,
  };
};

export const getEarliestFromDate = (from, dateRangeRestriction = -1) => {
  if (dateRangeRestriction < 1) {
    return from;
  }

  const lastAvailableDay = moment().subtract(dateRangeRestriction, 'day');

  if (from.isBefore(lastAvailableDay)) {
    return lastAvailableDay;
  }

  return from;
};

export const getOptionFromStorage = (
  storageKeyPrefix,
  options = getDefaultOptions(),
  dateRangeRestriction = -1,
) => {
  const option = getOption(
    xmSessionStorage.getItem(`${storageKeyPrefix}_filter`),
    options,
  );

  if (option === commonFilterOptions.DATE_RANGE) {
    const { from, to } = getDatesFromSessionStorage(storageKeyPrefix);
    const newOption = {
      getDateRangeGetter: () => () => ({
        from,
        to,
      }),
    };
    return defaults(newOption, option);
  }

  return {
    ...option,
    getDateRangeGetter: () => {
      const range = option.getDateRangeGetter()();
      return () => ({
        from: getEarliestFromDate(
          moment(range.from),
          dateRangeRestriction,
        ).format(),
      });
    },
  };
};

export const isDateOptionRestricted = (optionValue, dateRangeRestriction) => {
  return dateRangeRestriction !== -1 && dateRangeRestriction < optionValue;
};
